import { type FC, useEffect, useMemo, useState } from "react";

import { splitSummaryBySubmissions } from "./helpers";
import { ChangeSummary } from "./summary/ChangeSummary";
import { ExcludedPagesSection } from "./summary/ExcludedPagesSection";

import { serverGetPublicationSummary } from "api/api-server-publishing";
import { type PageChangeSummary, type PublicationSummary } from "api/api-types-publishing";
import { PublishConfirmationDialog } from "components/materials/section/publication/PublishConfirmationDialog";
import { t } from "i18n/i18n";
import { Card } from "mds/components/Card";
import { HorizontalDivider } from "mds/components/HorizontalDivider";
import { IconText } from "mds/components/IconText";
import { RoundedButton } from "mds/components/buttons/RoundedButton";
import { ArrowUpToLineIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { pageSelectors, selectCurrentSectionId } from "store/selectors";
import { useListSelector } from "store/store-hooks";
import { filterMap } from "utils/collections";

interface PublicationReviewProps {
  latestPublicationId: string;
}

export const PublicationReview: FC<PublicationReviewProps> = ({ latestPublicationId }) => {
  const [summary, setSummary] = useState<PublicationSummary | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const sectionId = useAppSelector(selectCurrentSectionId);
  const pages = useListSelector(pageSelectors.selectAll, ["id", "has_linked_submissions"]);
  const pageIdsWithLinkedSubmissions = useMemo(
    () =>
      new Set(
        filterMap(
          pages,
          (page) => !!page.has_linked_submissions,
          (page) => page.id,
        ),
      ),
    [pages],
  );

  useEffect(() => {
    serverGetPublicationSummary(latestPublicationId).then(setSummary);
  }, [latestPublicationId]);

  // Combine client-detected (has_linked_submissions in Redux) with server-detected
  // (deleted pages with orphaned linked submissions) to build the full exclusion set.
  const allExcludedIds = useMemo(() => {
    const ids = new Set(pageIdsWithLinkedSubmissions);
    summary?.excluded_pages.forEach((p) => ids.add(p.id));
    return ids;
  }, [pageIdsWithLinkedSubmissions, summary?.excluded_pages]);

  const { filteredSummary, pagesWithSubmissions } = useMemo(() => {
    if (!summary) return { filteredSummary: null, pagesWithSubmissions: [] as PageChangeSummary[] };
    return splitSummaryBySubmissions(summary, allExcludedIds);
  }, [summary, allExcludedIds]);

  // Merge client-detected (modified pages with has_linked_submissions) and
  // server-detected (deleted pages with orphaned linked submissions) into one list.
  const allExcludedPages = useMemo(() => {
    const existingIds = new Set(pagesWithSubmissions.map((p) => p.id));
    return [
      ...pagesWithSubmissions,
      ...(summary?.excluded_pages.filter((p) => !existingIds.has(p.id)) ?? []),
    ];
  }, [pagesWithSubmissions, summary?.excluded_pages]);

  if (!sectionId) return null;

  return (
    <>
      <Card
        className="flex flex-col gap-6 rounded-[12px] p-6 text-black-tint-20 supports-squircle:rounded-[24px] supports-squircle:corner-squircle"
        kind="gray"
      >
        <div className="flex items-center justify-between">
          <div>
            <h3>{t("publishing.review_title")}</h3>
            {summary && (
              <div className="body-s">
                {t("publishing.edits_by_authors", {
                  authors: summary.authors.join(", ") || "-",
                })}
              </div>
            )}
          </div>
          <RoundedButton kind="success" onClick={() => setIsConfirmOpen(true)}>
            <IconText iconStart={<ArrowUpToLineIcon />} text={t("publishing.publish_changes")} />
          </RoundedButton>
        </div>

        <HorizontalDivider />

        <ExcludedPagesSection
          description={t("publishing.student_submission_card.description")}
          pages={allExcludedPages}
          sectionId={sectionId}
        />

        {filteredSummary && (
          <ChangeSummary
            sectionId={sectionId}
            showExcludedPages={false}
            summary={filteredSummary}
          />
        )}
      </Card>

      <PublishConfirmationDialog
        open={isConfirmOpen}
        publicationId={latestPublicationId}
        onClose={() => setIsConfirmOpen(false)}
      />
    </>
  );
};
